import React, { useState, useEffect, Suspense, lazy, useRef } from "react";
import { backendUrl, API_KEYS } from '../config';
import DesktopWindow from "../components/DesktopWindow";
import SettingsPage from "./SettingsPage";
import UserManagementPage from "./UserManagementPage";
import DatabaseConfigPage from "./DatabaseConfigPage";
import LocalSettingsPage from "./LocalSettingsPage"; // Import LocalSettingsPage
import GroupLocalSettingsPage from "./GroupLocalSettingsPage"; // Import GroupLocalSettingsPage
import GroupsPage from "./GroupsPage"; // Import GroupsPage
import { useNavigate } from "react-router-dom";
import SettingsIcon from "@mui/icons-material/Settings";
import AppsIcon from "@mui/icons-material/Apps";
import StorageIcon from "@mui/icons-material/Storage";
import LanguageIcon from "@mui/icons-material/Language"; // Import LanguageIcon
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows"; // Import DesktopWindowsIcon
import FolderIcon from "@mui/icons-material/Folder"; // Import FolderIcon
import SaveIcon from "@mui/icons-material/Save"; // Import SaveIcon
import CalculateIcon from '@mui/icons-material/Calculate';
import PublicIcon from '@mui/icons-material/Public';
import NoteIcon from '@mui/icons-material/Note';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HelpPage from './HelpPage';
import SecurityIcon from '@mui/icons-material/Security'; // Import SecurityIcon
import { useLocalSettings } from '../contexts/LocalSettingsContext';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
import AppGroupApplicationsPage from "./AppGroupApplicationsPage";
import api from '../services/api';
import NetworkDrivesPage from "./NetworkDrivesPage";
import BackupSettingsPage from "./BackupSettingsPage"; // Import BackupSettingsPage
import AssociationSettingsPage from "./AssociationSettingsPage"; // Import AssociationSettingsPage
import { apiEvents } from '../services/api';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { TextField } from '@mui/material'; // Import TextField
import IAConfigPage from "./IAConfigPage"; // Import IAConfigPage
import FileExplorer from './FileExplorer';
import useMediaQuery from '@mui/material/useMediaQuery';

const desktopStyle = {
  width: "100vw",
  height: "100vh",
  background: "linear-gradient(120deg, #1976d2 0%, #90caf9 100%)",
  position: "relative",
  overflow: "hidden"
};
const iconStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  margin: 24,
  cursor: "pointer",
  color: "#fff",
  userSelect: "none",
  position: "absolute"
};
const taskbarStyle = {
  position: "absolute",
  left: 0,
  bottom: 0,
  width: "100%",
  height: 48,
  background: "rgba(30,30,30,0.95)",
  display: "flex",
  alignItems: "center",
  zIndex: 1000
};
const startMenuStyle = {
  position: "absolute",
  left: 16,
  bottom: 56,
  width: 220,
  background: "rgba(255,255,255,0.97)",
  boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
  borderRadius: 8,
  padding: 12,
  zIndex: 15,
  maxHeight: 400, // Limite la hauteur du menu démarrer
  overflow: 'visible' // On laisse visible ici, le scroll sera sur la liste interne
};

// Style spécifique pour les icônes importées (img) dans la barre des tâches
const taskbarImportedIconStyle = {
  width: 20,
  height: 20,
  display: "block",
  objectFit: "contain",
  marginBottom: 2,
  borderRadius: 8,
  background: '#fff'
};

// Jaune explorer
const explorerIcon = <FolderIcon style={{ color: '#FFC107', fontSize: 38, filter: 'drop-shadow(0 1px 2px #bbb)' }} />;

import { NotificationCenterProvider } from '../components/NotificationCenter';

export default function DesktopPage() {
  const navigate = useNavigate();

  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [windows, setWindows] = useState([]); // [{id, type, zIndex, position, size}]
  const [zCount, setZCount] = useState(1);
  const { settings } = useLocalSettings();
  const { t } = useTranslation();
  const [groupApps, setGroupApps] = useState([]); // Liste dynamique des apps du groupe d'application
  const [allApps, setAllApps] = useState([]); // Toutes les apps (pour mapping)
  const [currentUser, setCurrentUser] = useState(null);
  const [iconOptions, setIconOptions] = useState([]);
  const [minimizedWindows, setMinimizedWindows] = useState([]);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [iconPositions, setIconPositions] = useState({});
  // Ref pour toujours avoir la dernière version des positions
  const latestIconPositions = useRef(iconPositions);
  useEffect(() => { latestIconPositions.current = iconPositions; }, [iconPositions]);
  const [positionsLoaded, setPositionsLoaded] = useState(false);
  const [fileAssociations, setFileAssociations] = useState([]);
  const [fileAssociationsLoading, setFileAssociationsLoading] = useState(false);

  const isMobile = useMediaQuery('(max-width:600px)');

  // Charger les apps du groupe d'application au montage
  useEffect(() => {
    const appGroupId = window.localStorage.getItem('app_group_id');
    if (!appGroupId) return;
    // Récupérer toutes les apps (pour détails)
    api.get(`/applications/`).then(res => setAllApps(res.data));
    // Récupérer les apps du groupe
    api.get(`/app-groups/${appGroupId}/applications`, {
      headers: {
        'x-api-key': API_KEYS.key,
        'x-api-secret': API_KEYS.secret
      }
    }).then(res => setGroupApps(res.data));
  }, []);

  useEffect(() => {
    // Try to get user from localStorage (if you store it after login)
    const userStr = window.localStorage.getItem('currentUser');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    fetch(`${api.defaults.baseURL}/icons/icons`).then(r => r.json()).then(data => {
      setIconOptions(data);
      window.iconOptions = data;
    });
  }, []);

  useEffect(() => {
    function onSessionExpired(event) {
      if (event === 'session_expired') setShowSessionDialog(true);
    }
    apiEvents.on(onSessionExpired);
    return () => apiEvents.off(onSessionExpired);
  }, []);

  // Chargement initial des positions depuis le backend
  useEffect(() => {
    if (!currentUser) return;
    fetchIconPositions(currentUser).then(pos => {
      setIconPositions(pos || {});
      setPositionsLoaded(true);
    });
  }, [currentUser]);

  // Sauvegarde uniquement si les positions sont chargées et modifiées par l'utilisateur
  // Suppression de la sauvegarde automatique des positions à chaque changement
// useEffect(() => {
//   if (!currentUser || !positionsLoaded) return;
//   saveIconPositions(currentUser, iconPositions);
// }, [iconPositions, currentUser, positionsLoaded]);


  useEffect(() => {
    setFileAssociationsLoading(true);
    // Utilisation de backendUrl depuis config.js
    fetch(`${backendUrl}/api/config/associations`, {
      headers: {
        'x-api-key': API_KEYS.key,
        'x-api-secret': API_KEYS.secret,
        'accept': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data.associations)) {
          setFileAssociations(data.associations);
        }
      })
      .catch(() => {})
      .finally(() => setFileAssociationsLoading(false));
  }, [currentUser]);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = window.localStorage.getItem('token');
      if (!token) return;
      fetch(`${backendUrl}/auth/ping`, {
        headers: { Authorization: 'Bearer ' + token }
      })
      .then(res => {
        if (res.status === 401) {
          window.localStorage.removeItem('token');
          window.localStorage.removeItem('user');
          window.location.href = '/login';
        }
      })
      .catch(() => {});
    }, 2 * 60 * 1000); // toutes les 2 minutes
    return () => clearInterval(interval);
  }, []);

  // --- Redirige automatiquement vers la page de login si le JWT expire (token supprimé ou 401 détecté) ---
  useEffect(() => {
    // Vérifie à l'initialisation si le token est absent
    const token = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    // Patch fetch pour intercepter tous les 401
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401) {
        navigate('/login');
      }
      return response;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, [navigate]);

  function handleRelog() {
    window.localStorage.removeItem('token');
    window.sessionStorage.removeItem('token');
    setShowSessionDialog(false);
    navigate('/login');
  }

  // Fonction utilitaire pour obtenir l'icône à partir du champ icon (string ou icon_data)
  const getAppIconWithCustom = (icon, icon_data) => {
    // Normalize icon name for matching (case-insensitive)
    const normalizedIcon = icon ? icon.toLowerCase() : '';
    // 1. Affiche l'icône personnalisée si présente
    if (normalizedIcon === 'custom' && icon_data) {
      return <img src={icon_data} alt="icon" style={{width: 43.2, height: 43.2, objectFit: 'contain', verticalAlign: 'middle', borderRadius: 8, background: '#fff'}} />;
    }
    // 2. Recherche dans la liste des icônes personnalisées (iconOptions)
    if (iconOptions && Array.isArray(iconOptions)) {
      const opt = iconOptions.find(o => o.value.toLowerCase() === normalizedIcon);
      if (opt && opt.icon_data) {
        return <img src={opt.icon_data} alt={opt.label} style={{width: 43.2, height: 43.2, objectFit: 'contain', verticalAlign: 'middle', borderRadius: 8, background: '#fff'}} />;
      }
    }
    // 3. Sinon fallback sur les icônes locales
    switch (normalizedIcon) {
      case 'explorer':
      case 'folder':
      case 'directory':
        return <FolderIcon fontSize="large" sx={{ color: '#2196f3' }} />;
      case 'calculator':
        return <CalculateIcon fontSize="large" sx={{ color: '#43a047' }} />;
      case 'browser':
        return <PublicIcon fontSize="large" sx={{ color: '#1976d2' }} />;
      case 'notepad':
        return <NoteIcon fontSize="large" sx={{ color: '#fbc02d' }} />;
      case 'recyclebin':
        return <DeleteIcon fontSize="large" sx={{ color: '#b71c1c' }} />;
      case 'images':
        return <ImageIcon fontSize="large" sx={{ color: '#ab47bc' }} />;
      case 'videos':
        return <MovieIcon fontSize="large" sx={{ color: '#009688' }} />;
      case 'email':
        return <EmailIcon fontSize="large" sx={{ color: '#1976d2' }} />;
      case 'printer':
        return <PrintIcon fontSize="large" sx={{ color: '#616161' }} />;
      case 'desktop':
        return <DesktopWindowsIcon fontSize="large" sx={{ color: '#1976d2' }} />;
      case 'settings':
        return <SettingsIcon fontSize="large" sx={{ color: '#616161' }} />;
      case 'users':
        return <PeopleIcon fontSize="large" sx={{ color: '#795548' }} />;
      case 'security':
        return <SecurityIcon fontSize="large" sx={{ color: '#b71c1c' }} />;
      case 'appearance':
        return <ColorLensIcon fontSize="large" sx={{ color: '#ab47bc' }} />;
      case 'storage':
        return <StorageIcon fontSize="large" sx={{ color: '#009688' }} />;
      case 'file':
        return <InsertDriveFileIcon fontSize="large" sx={{ color: '#607d8b' }} />;
      default:
        return <AppsIcon fontSize="large" />;
    }
  };

  // Définition des apps disponibles
  const systemApps = [
    {
      type: 'explorer',
      label: t('desktop.explorer', 'Explorer / Explorateur'),
      icon: explorerIcon,
      windowTitle: t('desktop.file_explorer', 'Explorateur de fichiers / File Explorer'),
      main: true,
      content: () => <FileExplorer onOpenApp={handleOpenAppFromExplorer} associations={fileAssociations} currentUser={currentUser} />,
      defaultSize: { width: 800, height: 800 }
    },
    {
      type: 'help',
      label: t('desktop.help', 'Aide'),
      icon: <HelpOutlineIcon fontSize="large" sx={{ color: '#1976d2' }} />,
      windowTitle: t('desktop.help', 'Aide'),
      main: true,
      content: () => <HelpPage />, // Affiche la page d'aide dans une fenêtre
      defaultSize: { width: 600, height: 500 },
    },
    {
      type: "settings",
      label: t("desktop.settings"),
      icon: <SettingsIcon fontSize="large" />, 
      windowTitle: t("desktop.settings"),
      content: (onOpenApp, currentUser) => <SettingsPage onOpenApp={onOpenApp} currentUser={currentUser} />, 
      defaultSize: { width: 800, height: 800 },
      main: true
    },
    {
      type: "database-config",
      label: t("desktop.database"),
      icon: <StorageIcon fontSize="large" />, 
      windowTitle: t("desktop.database"),
      content: (currentUser) => <DatabaseConfigPage currentUser={currentUser} />, 
      defaultSize: { width: 700, height: 420 },
      main: false
    },
    {
      type: "apps",
      label: t("desktop.apps"),
      icon: <AppsIcon fontSize="large" />, 
      windowTitle: t("desktop.apps"),
      content: () => (
        <div style={{padding: isMobile ? 8 : 32}}>
          <h2 style={{fontSize: isMobile ? 20 : 28}}>{t("desktop.apps")}</h2>
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              gap: isMobile ? 12 : 24
            }}
          >
            {groupApps.map(app => (
              <div
                key={app.id}
                style={{
                  border: '1px solid #eee',
                  borderRadius: 8,
                  padding: isMobile ? 8 : 16,
                  minWidth: isMobile ? 100 : 140,
                  textAlign: 'center',
                  background: '#fafafa',
                  cursor: 'pointer',
                  marginBottom: isMobile ? 8 : 0
                }}
                onClick={() => openApp(`app-${app.id}`)}
              >
                {getAppIconWithCustom(app.icon, app.icon_data)}
                <div style={{ fontWeight: 600, marginTop: 8, fontSize: isMobile ? 15 : 18 }}>
                  {displayAppName(app.name)}
                </div>
                <div style={{ color: '#888', fontSize: isMobile ? 11 : 13, margin: '6px 0 0 0' }}>
                  {app.description}
                </div>
              </div>
            ))}
            {groupApps.length === 0 && (
              <div style={{ color: '#888', fontSize: isMobile ? 13 : 16 }}>{t('desktop.noApps')}</div>
            )}
          </div>
        </div>
      ),
      main: true,
      defaultSize: { width: 800, height: 480 }
    },
    {
      type: "users",
      label: t("desktop.users"),
      icon: <SettingsIcon fontSize="large" />, 
      windowTitle: t("desktop.users"),
      content: (currentUser) => <UserManagementPage currentUser={currentUser} />, 
      main: false // paramètres seulement
    },
    {
      type: "local-settings",
      label: t("desktop.localSettings"),
      icon: <LanguageIcon fontSize="large" />, 
      windowTitle: t("desktop.localSettings"),
      content: (currentUser) => <LocalSettingsPage currentUser={currentUser} />, 
      defaultSize: { width: 700, height: 420 },
      main: false // paramètres seulement
    },
    {
      type: "group-local-settings",
      label: t("desktop.groupLocalSettings"),
      icon: <LanguageIcon fontSize="large" color="secondary" />, 
      windowTitle: t("desktop.groupLocalSettings"),
      content: (props = {}) => <GroupLocalSettingsPage currentUser={props.currentUser || null} groupId={props.groupId || null} />, 
      defaultSize: { width: 700, height: 420 },
      main: false // paramètres seulement
    },
    {
      type: "groups",
      label: t("desktop.groups"),
      icon: <DesktopWindowsIcon fontSize="large" color="primary" />, 
      windowTitle: t("desktop.groups"),
      content: (currentUser) => <GroupsPage currentUser={currentUser} />, 
      defaultSize: { width: 700, height: 420 },
      main: false
    },
    {
      type: "application-settings",
      label: t("desktop.applicationSettings"),
      icon: <AppsIcon fontSize="large" color="primary" />, 
      windowTitle: t("desktop.applicationSettings"),
      content: (currentUser) => <ApplicationSettingsPage currentUser={currentUser} />,
      defaultSize: { width: 700, height: 420 },
      main: false // paramètres seulement
    },
    {
      type: "app-group-applications",
      label: t("desktop.appGroupApplications"),
      icon: <AppsIcon fontSize="large" color="secondary" />, 
      windowTitle: t("desktop.appGroupApplications"),
      content: (currentUser) => <AppGroupApplicationsPage currentUser={currentUser} />, 
      defaultSize: { width: 800, height: 600 },
      main: false // paramètres seulement
    },
    {
      type: "network-drives",
      label: t("settings.networkDrives"),
      icon: <StorageIcon fontSize="large" color="secondary" />,
      windowTitle: t("settings.networkDrives"),
      content: (currentUser) => <NetworkDrivesPage currentUser={currentUser} />,
      defaultSize: { width: 600, height: 450 },
      main: false
    },
    {
      type: "backup-settings",
      label: t("settings.backup"),
      icon: <SaveIcon fontSize="large" color="primary" />,
      windowTitle: t("settings.backup"),
      content: (currentUser) => <BackupSettingsPage currentUser={currentUser} />,
      defaultSize: { width: 480, height: 420 },
      main: false
    },
    {
      type: "association-settings",
      label: t("settings.association"),
      icon: <SaveIcon fontSize="large" color="primary" />,
      windowTitle: t("settings.association"),
      content: (currentUser, groupId) => <AssociationSettingsPage currentUser={currentUser} groupId={groupId} />, 
      defaultSize: { width: 480, height: 420 },
      main: false
    },
    {
      type: "ia-config",
      label: t("settings.ia-config"),
      icon: <SecurityIcon fontSize="large" color="secondary" />,
      windowTitle: t("settings.ia-config"),
      content: (currentUser) => <IAConfigPage currentUser={currentUser} />,
      defaultSize: { width: 600, height: 500 },
      main: false
    },
    {
      type: 'compose-mail',
      label: 'Nouveau message',
      icon: <EmailIcon fontSize="large" sx={{ color: '#1976d2' }} />,
      windowTitle: 'Nouveau message',
      main: false,
      content: (props) => <ComposeMailApp {...props} />,
      defaultSize: { width: 500, height: 420 },
    },
    {
      type: "notepadApp",
      name: "Bloc-notes",
      icon: <NoteIcon sx={{ color: '#1976d2' }} />, 
      width: 500,
      height: 400,
      defaultSize: { width: 500, height: 400 },
      description: 'Éditeur de texte simple',
      content: (props = {}) => {
        const NotepadApp = require('../apps/notepadApp.js').default;
        return <NotepadApp {...props} />;
      },
    },
  ];

  const availableApps = [
    ...systemApps,
    ...groupApps.map(app => ({
      type: `app-${app.id}`,
      label: app.name, // Affiche le nom réel de l'application
      icon: getAppIconWithCustom(app.icon, app.icon_data), // <-- Utilisation de la nouvelle fonction
      windowTitle: app.name,
      width: app.width,
      height: app.height,
      content: () => {
        // Render the app by dynamically importing and rendering the correct component based on the file name
        // If the app file exists and exports a default React component, render it dynamically
        try {
          // Try to require the app dynamically by name
          // The canonical file is: /src/apps/<AppName>App.js
          const componentName = `${app.name.replace(/\s+/g, '')}App`;
          const AppComponent = require(`../apps/${componentName}.js`).default;
          if (AppComponent) {
            return <AppComponent />;
          }
        } catch (e) {
          // fallback to previous logic for known apps
          // Toutes les apps sont désormais chargées dynamiquement via import()
        }
        return <div style={{padding:32}}><h2>{app.name}</h2><p>{app.description || ''}</p></div>;
      },
      main: true
    })),
  ];

  // Lazy load pour ApplicationSettingsPage
  const ApplicationSettingsPage = React.lazy(() => import("./ApplicationSettingsPage"));

  // Mapping utility for i18n language codes
  function mapLocaleToI18n(locale) {
    if (!locale) return 'fr';
    if (locale.startsWith('es')) return 'es';
    if (locale.startsWith('en')) return 'en';
    if (locale.startsWith('fr')) return 'fr';
    return 'fr'; // fallback
  }

  // Affichage du nom avec majuscule au début (utilitaire partagé)
  function displayAppName(name) {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  // Application automatique des paramètres globaux
  useEffect(() => {
    ////console.error('DesktopPage.js: settings.displayLanguage =', settings.displayLanguage);
    if (settings.displayLanguage) {
      i18n.changeLanguage(mapLocaleToI18n(settings.displayLanguage));
    }
    // Ici tu pourrais appliquer d'autres paramètres globaux (clavier, formats...)
    // selon les besoins de ton bureau/applications
  }, [settings]);

  // Ouvre une nouvelle fenêtre pour l'app donnée
  const openApp = (type, extraProps = {}) => {
    const app = availableApps.find(a => a.type === type);
    if (!app) return;
    const winId = `${type}-${Date.now()}`;
    // Détermine la taille par défaut
    let winSize = app.defaultSize || {};
    if (app.width && app.height) {
      winSize = { width: app.width, height: app.height };
    }
    // Pass currentUser for settings-related windows
    let propsToPass = { ...extraProps };
    if ([
      'settings',
      'group-local-settings',
      'local-settings',
      'users',
      'database-config',
      'application-settings',
      'network-drives',
      'backup-settings',
      'groups',
      'app-group-applications',
      'association-settings',
      'ia-config'
    ].includes(type)) {
      propsToPass.currentUser = currentUser;
    }
    setWindows(ws => [
      ...ws,
      {
        id: winId,
        type,
        zIndex: zCount + 1,
        position: { x: 80 + ws.length * 40, y: 80 + ws.length * 40 },
        size: winSize,
        ...propsToPass,
      },
    ]);
    setZCount(zCount + 1);
  };

  // Fonction pour ouvrir une fenêtre de composition sur le desktop
  function openComposeOnDesktop(data = {}) {
    const winId = `compose-${Date.now()}-${Math.floor(Math.random()*10000)}`;
    setWindows(ws => [
      ...ws,
      {
        id: winId,
        type: 'compose-mail',
        zIndex: zCount + 1,
        position: { x: 120 + Math.floor(Math.random()*100), y: 120 + Math.floor(Math.random()*100) },
        size: { width: 500, height: 420 },
        data,
        selectedAccountId: currentUser?.default_email_account_id,
      },
    ]);
    setZCount(zCount + 1);
  }

  // Expose la fonction d'ouverture à EmailApp (via props ou window)
  window.openComposeOnDesktop = openComposeOnDesktop;

  // Focus une fenêtre (passe devant)
  const focusWindow = (id) => {
    const newZ = zCount + 1;
    setWindows(ws => ws.map(w => w.id === id ? { ...w, zIndex: newZ } : w));
    setZCount(newZ);
  };

  // Ferme une fenêtre
  const closeWindow = (id) => setWindows(ws => ws.filter(w => w.id !== id));

  // Fonction pour ouvrir une app depuis FileExplorer
  const handleOpenAppFromExplorer = (appType, extraProps = {}) => {
    // Accepte filePath dans extraProps
    if (typeof appType === 'string' && appType.startsWith('app-')) {
      openApp(appType, extraProps);
    } else if (!isNaN(appType)) {
      // Si c'est un ID numérique, on convertit en app-<id>
      openApp(`app-${appType}`, extraProps);
    } else {
      openApp(appType, extraProps);
    }
  };

  // --- Définition dynamique des apps avec traduction ---
  const translatedApps = availableApps.map(app => {
    // Si c'est une app du groupe (type: app-<id>), affiche le nom réel
    if (app.type && app.type.startsWith('app-')) {
      return {
        ...app,
        label: displayAppName(app.label),
        windowTitle: displayAppName(app.label)
      };
    }
    // Sinon, traduction normale
    return {
      ...app,
      label: t(`desktop.${app.type}`),
      windowTitle: t(`desktop.${app.type}`)
    };
  });

  // Utilitaire pour obtenir uuid7 de l'utilisateur courant
  function getUserUuid7(user) {
    return user?.uuid7 || user?.id || null;
  }

  // --- API pour charger et sauvegarder les positions d'icônes ---
  async function fetchIconPositions(user) {
    const uuid7 = getUserUuid7(user);
    if (!uuid7) return {};
    try {
      const API_URL = backendUrl;
      const res = await fetch(`${API_URL}/api/config/positions/${uuid7}`);
      if (!res.ok) return {};
      const positions = await res.json();
      return positions || {};
    } catch (e) {}
    return {};
  }
  async function saveIconPositions(user, positions) {
    const uuid7 = getUserUuid7(user);
    if (!uuid7) return;
    const API_URL = process.env.REACT_APP_API_URL || '';
    await fetch(`${API_URL}/api/config/positions/${uuid7}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        key: 'desktop_icon_positions',
        value: JSON.stringify(positions)
      })
    });
  }

  // Positions des icônes synchronisées avec la base
  const dragIconRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragMovedRef = useRef(false);

  // Gestion du drag & drop
  function handleIconMouseDown(e, iconKey) {
    e.preventDefault(); // Empêche le drag natif navigateur
    dragIconRef.current = iconKey;
    const iconPos = iconPositions[iconKey] || { x: 40, y: 40 };
    dragOffsetRef.current = {
      x: e.clientX - iconPos.x,
      y: e.clientY - iconPos.y
    };
    dragMovedRef.current = false;
    window.addEventListener('mousemove', handleIconMouseMove);
    window.addEventListener('mouseup', handleIconMouseUp);
  }

  function handleIconMouseMove(e) {
    if (!dragIconRef.current) return;
    dragMovedRef.current = true;
    setIconPositions(pos => ({
      ...pos,
      [dragIconRef.current]: {
        x: Math.max(0, Math.min(e.clientX - dragOffsetRef.current.x, window.innerWidth - 80)),
        y: Math.max(0, Math.min(e.clientY - dragOffsetRef.current.y, window.innerHeight - 80))
      }
    }));
  }

  function handleIconMouseUp() {
    // Sauvegarde des positions UNIQUEMENT à la fin d'un déplacement d'icône
    if (dragIconRef.current && currentUser && positionsLoaded) {
      saveIconPositions(currentUser, latestIconPositions.current);
    }
    dragIconRef.current = null;
    setTimeout(() => { dragMovedRef.current = false; }, 0); // Correction : reset différé
    window.removeEventListener('mousemove', handleIconMouseMove);
    window.removeEventListener('mouseup', handleIconMouseUp);
  }

  const desktopIcons = translatedApps.filter(app => app.main).map((app, i) => {
    const key = app.type;
    const pos = iconPositions[key] || { x: 40 + (i % 6) * 100, y: 40 + Math.floor(i / 6) * 110 };
    return (
      <div
        key={key}
        style={{ ...iconStyle, left: pos.x, top: pos.y }}
        onMouseDown={e => handleIconMouseDown(e, key)}
        title={app.label}
        tabIndex={0}
        role="button"
        draggable={false}
        onClick={e => {
          // Only open the app if the icon was NOT dragged/moved
          if (dragMovedRef.current) {
            dragMovedRef.current = false; // Correction : reset direct
            return;
          }
          openApp(app.type);
        }}
      >
        {app.icon}
        <span style={{ fontSize: 14, color: '#fff', textShadow: '1px 1px 4px #1976d2' }}>{app.label}</span>
      </div>
    );
  });

  // Icônes menu démarrer (idem)
  const startMenuIcons = [...desktopIcons];

  // Ajout du bouton Déconnexion dans le menu démarrer
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Composant ComposeMailApp
  const ComposeMailApp = (props) => {
    const [data, setData] = React.useState(props.data || { to: '', subject: '', body: '', cc: '', bcc: '' });
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState('');

    // Corrige le blocage de saisie : synchronise le state avec props.data uniquement à l'ouverture ou changement de data
    React.useEffect(() => {
      if (props.data) {
        setData(props.data);
      }
    }, [props.data]);

    const handleSend = () => {
      setSending(true);
      setError('');
      window.axios.post(`${window.API_BASE}/email/send`, {
        account_id: props.selectedAccountId,
        ...data
      }, { headers: window.getAuthHeaders && window.getAuthHeaders() }).then(() => {
        if (props.onClose) props.onClose();
      }).catch(e => setError("Erreur lors de l'envoi du courriel")).finally(() => setSending(false));
    };
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <TextField fullWidth label="À" value={data.to} onChange={e => setData({ ...data, to: e.target.value })} sx={{ mb: 2 }} />
        <TextField fullWidth label="Sujet" value={data.subject} onChange={e => setData({ ...data, subject: e.target.value })} sx={{ mb: 2 }} />
        <TextField fullWidth label="Corps" multiline minRows={4} value={data.body} onChange={e => setData({ ...data, body: e.target.value })} sx={{ mb: 2 }} />
        <TextField fullWidth label="Cc" value={data.cc} onChange={e => setData({ ...data, cc: e.target.value })} sx={{ mb: 2 }} />
        <TextField fullWidth label="Bcc" value={data.bcc} onChange={e => setData({ ...data, bcc: e.target.value })} sx={{ mb: 2 }} />
        {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
          <Button onClick={props.onClose}>Annuler</Button>
          <Button variant="contained" onClick={handleSend} disabled={sending}>{sending ? 'Envoi...' : 'Envoyer'}</Button>
        </div>
      </div>
    );
  };

  return (
    <NotificationCenterProvider>
      <div style={desktopStyle}>
      {/* Icônes sur le bureau */}
      <div style={{ position: "absolute", top: 48, left: 32 }}>
        {desktopIcons}
      </div>
      {/* BARRE DES TACHES */}
      <div style={taskbarStyle}>
        {/* Bouton menu démarrer */}
        <button
          onClick={() => setStartMenuOpen(o => !o)}
          style={{
            background: "none",
            border: "none",
            color: "#fff",
            margin: "0 8px 0 16px",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            fontWeight: 600,
            fontSize: 12,
            outline: "none",
            cursor: "pointer"
          }}
          title={t("desktop.start")}
        >
          <AppsIcon fontSize="large" />
          <span style={{ fontSize: 10 }}>{t("desktop.start")}</span>
        </button>
        {/* Icônes des fenêtres ouvertes */}
        {windows.map(win => {
          const app = translatedApps.find(a => a.type === win.type);
          if (!app) return null;
          let icon = app.icon;
          if (React.isValidElement(icon) && icon.type === 'img') {
            icon = React.cloneElement(icon, { style: { width: 20, height: 20, objectFit: 'contain', borderRadius: 8, background: '#fff', ...icon.props.style } });
          }
          // Afficher seulement les icônes pour les fenêtres minimisées
          if (!minimizedWindows.includes(win.id)) return null;
          return (
            <button
              key={win.id}
              onClick={() => {
                setMinimizedWindows(mins => mins.filter(id => id !== win.id));
                focusWindow(win.id);
              }}
              style={{
                background: "none",
                border: "none",
                color: "#fff",
                margin: "0 8px",
                padding: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontWeight: 600,
                fontSize: 12,
                outline: "none",
                cursor: "pointer",
                opacity: 1
              }}
              title={app.windowTitle + ' (minimisée)'}
            >
              {icon}
              <span style={{ fontSize: 10 }}>{app.label}</span>
            </button>
          );
        })}
        {/* Icônes des fenêtres non minimisées (pour focus rapide) */}
        {windows.map(win => {
          const app = translatedApps.find(a => a.type === win.type);
          if (!app) return null;
          let icon = app.icon;
          if (React.isValidElement(icon) && icon.type === 'img') {
            icon = React.cloneElement(icon, { style: { width: 20, height: 20, objectFit: 'contain', borderRadius: 8, background: '#fff', ...icon.props.style } });
          }
          if (minimizedWindows.includes(win.id)) return null;
          return (
            <button
              key={win.id}
              onClick={() => focusWindow(win.id)}
              style={{
                background: "none",
                border: "none",
                color: "#fff",
                margin: "0 8px",
                padding: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontWeight: 600,
                fontSize: 12,
                outline: "none",
                cursor: "pointer",
                opacity: win.zIndex === zCount ? 1 : 0.7
              }}
              title={app.windowTitle}
            >
              {icon}
              <span style={{ fontSize: 10 }}>{app.label}</span>
            </button>
          );
        })}
      </div>
      {/* Fenêtres ouvertes */}
      {windows.map(win => {
        const app = translatedApps.find(a => a.type === win.type);
        if (!app) return null;
        let content;
        if (typeof app.content === 'function') {
          if (app.type === 'settings') {
            content = app.content(openApp, win.currentUser || currentUser);
          } else if (app.type === 'group-local-settings') {
            content = app.content({ currentUser: win.currentUser || currentUser, groupId: win.groupId });
          } else if ([
            'local-settings',
            'users',
            'database-config',
            'application-settings',
            'network-drives',
            'backup-settings',
            'groups',
            'app-group-applications',
            'association-settings',
            'ia-config'
          ].includes(app.type)) {
            content = app.content(win.currentUser || currentUser);
          } else if (app.type === 'notepadApp') {
            content = app.content(win);
          } else {
            content = app.content();
          }
        } else {
          content = app.content;
        }
        return (
          <DesktopWindow
            key={win.id}
            id={win.id}
            title={app.windowTitle}
            position={win.position}
            size={app.defaultSize || win.size}
            zIndex={win.zIndex}
            onClose={() => closeWindow(win.id)}
            onFocus={() => focusWindow(win.id)}
            minWidth={app.defaultSize?.width || 350}
            minHeight={app.defaultSize?.height || 200}
            allowFullscreen={true}
            onMinimize={() => setMinimizedWindows(mins => [...new Set([...mins, win.id])])}
            minimized={minimizedWindows.includes(win.id)}
          >
            <Suspense fallback={<div>Chargement…</div>}>
              {content}
            </Suspense>
          </DesktopWindow>
        );
      })}
      {/* Menu démarrer */}
      {startMenuOpen && (
        <div style={{ ...startMenuStyle, zIndex: 9999, position: 'fixed' }}>
          <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 8 }}>
            {translatedApps.filter(app => app.main).map(app => (
              <div
                key={app.type}
                style={{ display: "flex", alignItems: "center", padding: 8, cursor: "pointer", borderRadius: 5, gap: 10, fontWeight: 500 }}
                onClick={() => {
                  openApp(app.type);
                  setStartMenuOpen(false);
                }}
              >
                {app.icon}
                <span>{app.label}</span>
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid #eee", margin: "8px 0" }} />
          <div
            style={{ display: "flex", alignItems: "center", padding: 8, cursor: "pointer", borderRadius: 5, gap: 10, color: "#c00", fontWeight: 500 }}
            onClick={handleLogout}
          >
            <span style={{ fontWeight: 700 }}>⏻</span>
            <span>{t("desktop.logout")}</span>
          </div>
        </div>
      )}
      {/* Dialog de session expirée */}
      <Dialog open={showSessionDialog} onClose={handleRelog}>
        <DialogTitle>Session terminée</DialogTitle>
        <DialogContent>
          Votre session a expiré. Veuillez vous reconnecter pour continuer.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRelog} variant="contained" color="primary">Se relogger</Button>
        </DialogActions>
      </Dialog>
    </div>
    </NotificationCenterProvider>
  );
}
